import axios, {
   type AxiosError,
   type AxiosInstance,
   type InternalAxiosRequestConfig,
} from 'axios';
import Cookies from 'universal-cookie';

import { checkJWT } from '@/utils/common';

import AuthService from './authService';
import ClientService from './clientService';
import EventService from './eventService';
import GradedSurveyService from './gradedSurveyService';
import PageService from './pageService';
import QuestionChoiceService from './questionChoiceService';
import QuestionService from './questionService';
import QuestionTypeService from './questionTypeService';
import SessionService from './sessionService';
import SharesService from './sharesService';
import SiteService from './siteService';
import StatisticsService from './statisticsService';
import StatusService from './statusService';
import SurveyResponsesService from './surveyResponsesService';
import SurveyService from './surveyService';
import SurveyTypeService from './surveyTypeService';
import TrophyService from './trophyService';
import UserService from './userService';

let isRefreshingToken = false;
const JWT_COOKIE_CONFIG = { domain: process.env.DOMAIN, path: '/' };
const LOGIN_REDIRECT_URL = `${process.env.PORTAIL_URL}/auth`;

class Service {
   private static instance: Service;
   auth: AuthService;
   provider: AxiosInstance;
   user: UserService;
   survey: SurveyService;
   status: StatusService;
   session: SessionService;
   questionType: QuestionTypeService;
   question: QuestionService;
   questionChoice: QuestionChoiceService;
   client: ClientService;
   responseRate: SurveyResponsesService;
   event: EventService;
   shares: SharesService;
   surveyType: SurveyTypeService;
   page: PageService;
   site: SiteService;
   statistics: StatisticsService;
   gradedSurvey: GradedSurveyService;
   trophy: TrophyService;

   constructor() {
      this.provider = axios.create({
         baseURL: process.env.API_URL,
         timeout: 90000,
         headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json; charset=utf-8',
         },
      });

      this.provider.interceptors.request.use((config) =>
         this.attachAuthorizationHeaderToRequest(config),
      );

      this.provider.interceptors.response.use(
         (response) => response,
         (error) => this.handleUnauthenticatedResponse(error),
      );

      this.auth = new AuthService();
      this.user = new UserService(this.provider);
      this.survey = new SurveyService(this.provider);
      this.status = new StatusService(this.provider);
      this.session = new SessionService(this.provider);
      this.client = new ClientService(this.provider);
      this.questionType = new QuestionTypeService(this.provider);
      this.question = new QuestionService(this.provider);
      this.questionChoice = new QuestionChoiceService(this.provider);
      this.responseRate = new SurveyResponsesService(this.provider);
      this.event = new EventService(this.provider);
      this.shares = new SharesService(this.provider);
      this.surveyType = new SurveyTypeService(this.provider);
      this.page = new PageService(this.provider);
      this.site = new SiteService(this.provider);
      this.statistics = new StatisticsService(this.provider);
      this.gradedSurvey = new GradedSurveyService(this.provider);
      this.trophy = new TrophyService(this.provider);
   }

   private async attachAuthorizationHeaderToRequest(
      config: InternalAxiosRequestConfig,
   ) {
      const cookies = new Cookies();
      let jwtToken = cookies.get<string>('jwt');

      if (!jwtToken) return config;

      const jwtStatus = checkJWT(jwtToken);

      if (jwtStatus === 'refreshable' && !isRefreshingToken) {
         isRefreshingToken = true;
         try {
            jwtToken = await this.auth.refreshToken(jwtToken);
            cookies.set('jwt', jwtToken, JWT_COOKIE_CONFIG);
         } catch (refreshError) {
            cookies.remove('jwt', JWT_COOKIE_CONFIG);
            return Promise.reject(refreshError);
         } finally {
            isRefreshingToken = false;
         }
      }

      config.headers.Authorization = `bearer ${jwtToken}`;
      return config;
   }

   private async handleUnauthenticatedResponse(error: AxiosError) {
      if (error.status === 401 && !isRefreshingToken) {
         const cookies = new Cookies();
         cookies.remove('jwt', JWT_COOKIE_CONFIG);
         window.location.href = LOGIN_REDIRECT_URL;
      }

      return Promise.reject(error);
   }

   static getInstance(): Service {
      if (!Service.instance) {
         Service.instance = new Service();
      }
      return Service.instance;
   }
}

export default Service.getInstance();
